import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

import { useMemoryStore, MEMORY_LIMITS, Memory } from "src/ai/memories/memoryStore";

/**
 * MemoriesDialog gives the user direct read/edit/delete access to Kate's
 * memory store. Kate can also manage memories via her tool calls, but having
 * a UI is important because:
 *   - Memories drift stale and the user needs an easy way to prune them.
 *   - The user might add a memory without going through chat (e.g. on first
 *     login: "always exclude install runs").
 *   - Trust — every memory is shown plainly, no surprise injections into
 *     the system prompt.
 */

interface Props {
  open: boolean;
  onClose: () => void;
}

interface DraftRowProps {
  memory: Memory;
  onSave: (id: string, patch: { title: string; content: string }) => void;
  onDelete: (id: string) => void;
}

const DraftRow: React.FC<DraftRowProps> = ({ memory, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(memory.title);
  const [content, setContent] = useState(memory.content);

  const cancel = () => {
    setTitle(memory.title);
    setContent(memory.content);
    setEditing(false);
  };

  const save = () => {
    if (!title.trim() || !content.trim()) return;
    onSave(memory.id, { title: title.trim(), content: content.trim() });
    setEditing(false);
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: 1,
        p: 1.25,
        mb: 1,
        backgroundColor: "background.paper",
      }}
    >
      {editing ? (
        <Stack spacing={1}>
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, MEMORY_LIMITS.MAX_TITLE_LENGTH))}
            inputProps={{ maxLength: MEMORY_LIMITS.MAX_TITLE_LENGTH }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            label="Memory"
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, MEMORY_LIMITS.MAX_CONTENT_LENGTH))}
            inputProps={{ maxLength: MEMORY_LIMITS.MAX_CONTENT_LENGTH }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button size="small" startIcon={<CancelIcon fontSize="small" />} onClick={cancel}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={save}
              disabled={!title.trim() || !content.trim()}
            >
              Save
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {memory.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", color: "text.secondary", mt: 0.25 }}
            >
              {memory.content}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(memory.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const MemoriesDialog: React.FC<Props> = ({ open, onClose }) => {
  const memories = useMemoryStore((s) => s.memories);
  const addMemory = useMemoryStore((s) => s.add);
  const updateMemory = useMemoryStore((s) => s.update);
  const removeMemory = useMemoryStore((s) => s.remove);
  const clearAll = useMemoryStore((s) => s.clearAll);

  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");

  const handleCreate = () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    addMemory({ title: newTitle.trim(), content: newContent.trim() });
    setNewTitle("");
    setNewContent("");
    setCreating(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 2100 }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Kate&apos;s memories
        <Typography variant="caption" component="div" color="text.secondary">
          {memories.length} of {MEMORY_LIMITS.MAX_MEMORIES} • These are injected
          into every conversation so Kate doesn&apos;t forget your preferences.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 240 }}>
        {memories.length === 0 && !creating && (
          <Typography variant="body2" color="text.secondary">
            No memories yet. Tell Kate &quot;remember that...&quot; in chat, or add
            one manually.
          </Typography>
        )}

        {memories.map((m) => (
          <DraftRow
            key={m.id}
            memory={m}
            onSave={(id, patch) => updateMemory(id, patch)}
            onDelete={(id) => removeMemory(id)}
          />
        ))}

        {creating && (
          <Box
            sx={{
              border: "1px dashed rgba(0,0,0,0.25)",
              borderRadius: 1,
              p: 1.25,
              mt: 1,
            }}
          >
            <Stack spacing={1}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Title"
                value={newTitle}
                onChange={(e) =>
                  setNewTitle(e.target.value.slice(0, MEMORY_LIMITS.MAX_TITLE_LENGTH))
                }
                inputProps={{ maxLength: MEMORY_LIMITS.MAX_TITLE_LENGTH }}
                placeholder="e.g. Default time window"
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                label="Memory"
                value={newContent}
                onChange={(e) =>
                  setNewContent(
                    e.target.value.slice(0, MEMORY_LIMITS.MAX_CONTENT_LENGTH),
                  )
                }
                inputProps={{ maxLength: MEMORY_LIMITS.MAX_CONTENT_LENGTH }}
                placeholder="e.g. Always default to the last 30 days unless I say otherwise."
              />
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  startIcon={<CancelIcon fontSize="small" />}
                  onClick={() => {
                    setCreating(false);
                    setNewTitle("");
                    setNewContent("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon fontSize="small" />}
                  onClick={handleCreate}
                  disabled={!newTitle.trim() || !newContent.trim()}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1.5 }}>
        <Box>
          {memories.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete all ${memories.length} memories? This can't be undone.`,
                  )
                ) {
                  clearAll();
                }
              }}
            >
              Clear all
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!creating && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreating(true)}
              disabled={memories.length >= MEMORY_LIMITS.MAX_MEMORIES}
            >
              Add memory
            </Button>
          )}
          <Button size="small" variant="contained" onClick={onClose}>
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
